import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useDebouncedSearch } from '../hooks/useNavBarSearch';
import { Search } from 'lucide-react';
import { numberFormatter } from "@ecommerce/shared-utils";
import { SEARCH_FIELD_HEIGHT } from './NavbarConstants';
import { BUTTON_STYLES } from '../../../styles/buttonStyles';
import { TEXTFIELD_STYLES } from '../../../styles/textFieldStyles';

interface SearchbarProps {
  onClose?: () => void;
}

export default function Searchbar({ onClose }: SearchbarProps) {
  const navigate = useNavigate();
  
  const {
    searchTerm,
    setSearchTerm,
    suggestions,
    loading,
    isOpen,
    setIsOpen,
    clearSearch
  } = useDebouncedSearch();

  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleClearSearch = () => {
    clearSearch();
  };

  const handleSuggestionClick = (suggestion: any) => {
    navigate(`/productos/${suggestion.sku}`);
    setIsOpen(false);
    setSearchTerm('');
    if (onClose) onClose();
  };

  const handleSeeAllResults = () => {
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    setIsOpen(false);
    setSearchTerm('');
    if (onClose) onClose();
  };

  const displayedSuggestions = suggestions.suggestions.slice(0, 4);
  const showSuggestions = isOpen && (displayedSuggestions.length > 0 || loading);

  return (
    <Box 
      ref={searchRef} 
      sx={{ 
        position: 'relative', 
        width: '100%',
      }}
    >
      <TextField
        placeholder="Buscar"
        value={searchTerm}
        onChange={handleSearchChange}
        onKeyDown={(e) => {
            if (e.key === "Enter" && showSuggestions) {
              e.preventDefault();
              handleSeeAllResults();
            }
        }}
        size="small"
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            height: SEARCH_FIELD_HEIGHT,
            ...TEXTFIELD_STYLES.search,
          },
        }}
        InputProps={{ 
          endAdornment: (
            <InputAdornment position="end">
              {loading ? (
                <CircularProgress size={20} />
              ) : searchTerm ? (
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              ) : (
                <Search size={20} />
              )}
            </InputAdornment>
          ),
          sx: {
            fontSize: '0.8rem',
            color: 'grey.600',
            borderRadius: 0,
          }
        }}
      />

      {showSuggestions && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            mt: 0.5,
            maxHeight: 400,
            overflow: 'auto',
            borderRadius: 0,
            width: '100%',
          }}
        >
          {loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <List sx={{ py: 0 }}>
                {displayedSuggestions.map((suggestion, index) => (
                  <ListItem key={suggestion.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderRadius: 0,
                        '&:hover': {
                          backgroundColor: 'grey.50'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, mr: 2 }}>
                        {suggestion.image && (
                          <Box
                            component="img"
                            src={suggestion.image}
                            alt={suggestion.name}
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover'
                            }}
                          />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              fontFamily: '"Rubik", sans-serif',
                              fontSize: '0.8rem'
                            }}
                          >
                            {suggestion.name}
                          </Typography>
                        }
                        secondary={
                          suggestion.price && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.primary',
                                fontWeight: 600,
                                fontFamily: '"Rubik", sans-serif',
                                fontSize: '0.8rem'
                              }}
                            >
                              {numberFormatter.formatPrice(suggestion.price)}
                            </Typography>
                          )
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {suggestions.suggestions.length > 4 && (
                <>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleSeeAllResults}
                      sx={BUTTON_STYLES.primary}
                    >
                      VER TODOS LOS RESULTADOS
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}














